import React from "react";
import {
  Badge,
  Box,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { clientAPI } from "../../api/client";
import { formatPrice } from "../../utils/utils";

const RANK_STYLE = {
  "Kim cương": { bg: "#E0F2FE", color: "#0284C7" },
  Vàng: { bg: "#FEF3C7", color: "#D97706" },
  Bạc: { bg: "#F1F5F9", color: "#64748B" },
  "Thành viên": { bg: "#F3F4F6", color: "#6B7280" },
};

const formatDate = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "-" : d.toLocaleDateString("vi-VN");
};

const RankBadge = ({ rank }) => {
  const style = RANK_STYLE[rank] || RANK_STYLE["Thành viên"];
  return (
    <Box
      display="inline-block"
      p={"4px 12px"}
      borderRadius={"999px"}
      backgroundColor={style.bg}
      color={style.color}
      fontSize={{ base: "13px" }}
      fontWeight={600}
    >
      {rank}
    </Box>
  );
};

const InfoRow = ({ label, children }) => (
  <Flex gap={"8px"} fontSize={"14px"}>
    <Text color={"#51607B"} minW={"140px"}>
      {label}
    </Text>
    <Box color={"#293755"} fontWeight={500}>
      {children}
    </Box>
  </Flex>
);

const ProjectChips = ({ items }) => {
  if (!items?.length) return <Text color={"#51607B"}>-</Text>;
  return (
    <Flex gap={"8px"} flexWrap={"wrap"}>
      {items.map((p) => (
        <Badge
          key={p._id}
          textTransform={"none"}
          backgroundColor={"#EEF2FF"}
          color={"#3730A3"}
          borderRadius={"6px"}
          p={"4px 10px"}
          fontWeight={500}
        >
          {p.name || p.code || "-"}
        </Badge>
      ))}
    </Flex>
  );
};

const SectionTitle = ({ children }) => (
  <Text fontSize={"15px"} fontWeight={700} color={"#293755"} mb={"8px"}>
    {children}
  </Text>
);

export default function DetailCustomerCare({ isOpen, onClose, customerId }) {
  const { data, isLoading } = useQuery({
    queryKey: ["CustomerCareDetail", customerId],
    queryFn: () => clientAPI("get", `/customer/customer-care/${customerId}`),
    enabled: Boolean(isOpen && customerId),
    refetchOnWindowFocus: false,
    staleTime: 30 * 1000,
  });

  const customer = data?.customer;
  const ranking = data?.ranking;
  const transactions = data?.transactions || [];

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={"6xl"} isCentered>
      <ModalOverlay />
      <ModalContent borderRadius={"16px"} maxH={"90vh"} overflowY={"auto"}>
        <ModalHeader fontSize={"20px"} fontWeight={600}>
          Chi tiết chăm sóc khách hàng
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={"24px"}>
          {isLoading ? (
            <Flex justify={"center"} align={"center"} py={"40px"}>
              <Spinner />
            </Flex>
          ) : !customer ? (
            <Text py={"24px"} textAlign={"center"}>
              Không có dữ liệu
            </Text>
          ) : (
            <Flex flexDirection={"column"} gap={"24px"}>
              {/* Thông tin cơ bản */}
              <Box>
                <SectionTitle>Thông tin khách hàng</SectionTitle>
                <Flex flexDirection={"column"} gap={"8px"}>
                  <InfoRow label={"Tên khách hàng"}>
                    {customer.name || "-"}
                  </InfoRow>
                  <InfoRow label={"Mã KH"}>{customer.code || "-"}</InfoRow>
                  <InfoRow label={"Số điện thoại"}>
                    {customer.phone || "-"}
                  </InfoRow>
                  <InfoRow label={"Email"}>{customer.email || "-"}</InfoRow>
                  <InfoRow label={"Phân loại"}>
                    {customer.customerType?.name || "-"}
                  </InfoRow>
                  <InfoRow label={"NV phụ trách"}>
                    {customer.employee?.fullName || "-"}
                  </InfoRow>
                </Flex>
              </Box>

              {/* Xếp hạng */}
              <Box>
                <SectionTitle>Xếp hạng</SectionTitle>
                <Flex gap={"16px"} alignItems={"center"} flexWrap={"wrap"}>
                  <RankBadge rank={ranking?.rank} />
                  <Text fontSize={"14px"} fontWeight={600} color={"#293755"}>
                    {formatPrice(ranking?.rankScore || 0)} đ
                  </Text>
                  <Text fontSize={"13px"} color={"#51607B"}>
                    (Số giao dịch: {ranking?.transactionCount || 0} · Dự án quan
                    tâm: {ranking?.wishlistCount || 0} · Dự án đã giao dịch:{" "}
                    {ranking?.transactedProjectCount || 0})
                  </Text>
                </Flex>
                {/* <Text fontSize={"12px"} color={"#8A94A6"} mt={"6px"}>
                  Điểm tính theo số điện thoại, gộp các giao dịch trong 1 năm kể
                  từ giao dịch đầu tiên ({formatDate(ranking?.firstLockingTime)}
                  {ranking?.windowEnd
                    ? ` → ${formatDate(ranking.windowEnd)}`
                    : ""}
                  ).
                </Text> */}
                <Text fontSize={"12px"} color={"#8A94A6"} mt={"6px"}>
                  {" "}
                  {formatDate(ranking?.firstLockingTime)}
                  {ranking?.windowEnd
                    ? ` → ${formatDate(ranking.windowEnd)}`
                    : ""}
                </Text>
              </Box>

              {/* Dự án quan tâm */}
              <Box>
                <SectionTitle>Dự án quan tâm</SectionTitle>
                <ProjectChips items={data?.wishlistProjects} />
              </Box>

              {/* Dự án đã giao dịch */}
              <Box>
                <SectionTitle>Dự án đã giao dịch</SectionTitle>
                <ProjectChips items={data?.transactedProjects} />
              </Box>

              {/* Danh sách giao dịch */}
              <Box>
                <SectionTitle>Giao dịch ({transactions.length})</SectionTitle>
                <TableContainer
                  border={"1px solid var(--Line)"}
                  borderRadius={"8px"}
                >
                  <Table variant={"simple"} size={"sm"}>
                    <Thead backgroundColor={"#F9FAFD"}>
                      <Tr>
                        <Th>STT</Th>
                        <Th>Mã căn</Th>
                        <Th>Tên căn</Th>
                        <Th>Dự án</Th>
                        <Th>Ngày cọc</Th>
                        <Th isNumeric>Giá tính PDV</Th>
                        <Th isNumeric>Giá tính PDV tạm tính</Th>
                        <Th isNumeric>Hệ số K</Th>
                        <Th isNumeric>Tỉ lệ nhận về</Th>
                        <Th isNumeric>Giá trị</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {transactions.length ? (
                        transactions.map((t, index) => (
                          <Tr key={t._id || index}>
                            <Td>{index + 1}</Td>
                            <Td>
                              {t._id && t.apartment?.code ? (
                                <Text
                                  as="span"
                                  color={"var(--color-option-employee-hover)"}
                                  textDecoration={"underline"}
                                  cursor={"pointer"}
                                  fontWeight={500}
                                  onClick={() =>
                                    window.open(
                                      `/locking/calculateServiceFees/${t._id}`,
                                      "_blank",
                                      "noopener,noreferrer",
                                    )
                                  }
                                >
                                  {t.apartment.code}
                                </Text>
                              ) : (
                                t.apartment?.code || "-"
                              )}
                            </Td>
                            <Td>{t.apartment?.name || "-"}</Td>
                            <Td>{t.project?.name || "-"}</Td>
                            <Td>{formatDate(t.bookingTime)}</Td>
                            <Td isNumeric>
                              {formatPrice(t.officialPdvPrice || 0)}
                            </Td>
                            <Td isNumeric>
                              {formatPrice(t.provisionalPdvPrice || 0)}
                            </Td>
                            <Td isNumeric>{t.kRate ?? "-"}</Td>
                            <Td isNumeric>
                              {Math.round((t.receiveRate || 0) * 100)}%
                            </Td>
                            <Td isNumeric>
                              {formatPrice(t.contribution || 0)}
                            </Td>
                          </Tr>
                        ))
                      ) : (
                        <Tr>
                          <Td colSpan={10} textAlign={"center"} py={"20px"}>
                            Chưa có giao dịch
                          </Td>
                        </Tr>
                      )}
                    </Tbody>
                  </Table>
                </TableContainer>
              </Box>
            </Flex>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
